/**
 * PLITS catalogue templates use names like PLITS_TEM_POL_<title>_<control>_<ver>.docx
 * or PLITS_TEM_<title with spaces>_<control>_<ver>.docx
 * In onboarding/org flows we show the compact company name in the template file column
 * and a readable artefact name with only the literal PLITS_TEM_ prefix removed.
 */

import { stripTrailingDocumentExtension } from "./artefactDisplayFileName";

/** Strip whitespace only; keep punctuation such as parentheses in legal names. */
export function compactCompanyNameForTemplateFile(companyDisplayName) {
  return String(companyDisplayName || "").replace(/\s+/g, "");
}

/** Legacy: PLITS_TEM_<KIND>_ with KIND alphanumeric (e.g. POL, PRO). */
const PLITS_CATALOGUE_KIND_PREFIX_RE = /^PLITS_TEM_[A-Z0-9]+_/i;

/**
 * Removes a single leading literal `PLITS_TEM_` from the basename segment (case-insensitive).
 * Does not strip `PRO_` / `POL_` after that — those stay for Artefact name when using this alone.
 * @param {string} segment — last path segment / basename only
 */
export function stripLeadingPlitsTemUnderscore(segment) {
  const last = String(segment || "");
  const m = last.match(/^PLITS_TEM_/i);
  if (!m) return last;
  return last.slice(m[0].length);
}

/**
 * Artefact name column: last segment minus leading `PLITS_TEM_`, then strip .docx etc.
 * Example: PLITS_TEM_Application Security Requirements_A0826_V1.docx → Application Security Requirements_A0826_V1
 * @param {string} rawPathOrBase — may include path separators
 */
export function catalogueArtefactNameFromBlobPath(rawPathOrBase) {
  const raw = String(rawPathOrBase || "").trim();
  if (!raw) return "";
  const last = raw.split(/[/\\]/).pop() || raw;
  const peeled = stripLeadingPlitsTemUnderscore(last);
  return stripTrailingDocumentExtension(peeled);
}

/**
 * @param {string} companyDisplayName
 * @param {string} rawPathOrBase — may include path separators; only the last segment is transformed
 * @returns {string} final basename (no directories). If no PLITS prefix, returns last segment unchanged.
 * If PLITS prefix matches but company compact is empty, returns last segment unchanged (keeps PLITS name).
 */
export function renamePlitsCatalogueTemplateBasename(companyDisplayName, rawPathOrBase) {
  const raw = String(rawPathOrBase || "");
  if (!raw) return "";
  const last = raw.split(/[/\\]/).pop() || raw;
  const compact = compactCompanyNameForTemplateFile(companyDisplayName);

  const kindMatch = last.match(PLITS_CATALOGUE_KIND_PREFIX_RE);
  if (kindMatch) {
    const rest = last.slice(kindMatch[0].length);
    if (!compact) return last;
    return compact + rest;
  }

  if (/^PLITS_TEM_/i.test(last)) {
    const rest = last.replace(/^PLITS_TEM_/i, "");
    if (!compact) return last;
    return compact + rest;
  }

  return last;
}

/**
 * When fileName or templateId uses a PLITS basename, set both to the same renamed basename.
 * If no PLITS pattern, leaves the row unchanged (avoids forcing templateId === fileName for custom rows).
 * @param {Record<string, unknown>} row
 * @param {string} companyDisplayName
 * @returns {Record<string, unknown>}
 */
export function normalizeArtefactIndexRowNames(row, companyDisplayName) {
  if (!row || typeof row !== "object") return row;
  const source = row.fileName || row.templateId || "";
  if (!source) return { ...row };
  const last = String(source).split(/[/\\]/).pop() || String(source);
  const renamed = renamePlitsCatalogueTemplateBasename(companyDisplayName, source);
  if (renamed === last) return { ...row };
  return { ...row, fileName: renamed, templateId: renamed };
}

/**
 * @param {unknown[]} rows
 * @param {string} companyDisplayName
 * @returns {Record<string, unknown>[]}
 */
export function normalizeArtefactIndexRows(rows, companyDisplayName) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => normalizeArtefactIndexRowNames(r, companyDisplayName));
}
